import * as Constants from '../constants/';
import { lastXDays } from '../../common/utilities/dateRangeHelper';

const userRange = 1;
const initialState = {
  isLoading: false,
  isSaving: false,
  hasLoadedOnce: false,
  loadContext: {},
  saveContext: {},
  error: null,
  data: {
    archived: {},
    experiments: {},
    browsedFilter: {},
    search: {},
    analytics: {
      filter: {
        ...lastXDays(userRange).dates
      },
      jobs: [],
      performance: {
        performers: [],
        details: [],
        summary: {}
      }
    }
  }
};

/**
 * Concatenates two lists of assets together, respecting UUID uniqueness
 * If there is a conflict, the asset in the first list will be preserved and the
 * asset in the second list will be discarded.
 *
 * @param original
 * @param additional
 */
function mergeExperimentLists(original, additional) {
  const originalIds = original.map(experiment => experiment.uuid);
  const additionalIds = additional.map(experiment => experiment.uuid);
  const duplicateIds = originalIds.filter(id => additionalIds.includes(id));
  if (duplicateIds.length) {
    // How to handle the conflicts?
    // Do we want to keep the original? replace the original? keep the new?
    // For now, keeping the original and discarding the additional
    const nonConflicting = additional.filter(
      experiment => !duplicateIds.includes(experiment.uuid)
    );
    return [...original, ...nonConflicting];
  } else {
    return [...original, ...additional];
  }
}

/**
 * Updates a list of experiments with a new experiment. Behavior will be different depending on
 * edit vs. create vs. archive/publish/end
 *
 * When creating a new experiment, the experiment will be pre-pended to the list
 * When editing an experiment, the matching experiment in the list will be found and updated
 * (If no experiment could be found a copy of the original list is returned)
 *
 * @param assets
 * @param action
 */
function updateExperimentInList(experiments, action) {
  if (action.mode === 'create') {
    return [action.data, ...experiments];
  } else if (action.mode === 'archive' || action.mode === 'delete') {
    //remove the experiment
    const uuid = action.data.uuid ? action.data.uuid : action.experiment.uuid;
    return experiments.filter(a => a.uuid !== uuid);
  } else {
    //find the experiment
    const index = experiments.map(a => a.uuid).indexOf(action.data.uuid);
    if (index >= 0) {
      const arr = experiments.slice(0);
      arr[index] = { ...action.data };
      return arr;
    } else {
      console.warn('Could not find experiment to update');
      return [...experiments];
    }
  }
}

export default function(state = initialState, action) {
  const loadContext = {};
  const saveContext = { ...state.saveContext };
  const context = action.load?.context || action.context;

  switch (action.type) {
    case Constants.REQUEST_EXPERIMENT:
      if (context) {
        loadContext[context] = true;
      }
      return {
        ...state,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.RECEIVE_EXPERIMENT:
      if (context) {
        loadContext[context] = false;
      }

      if (action.error) {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          error: action.error
        };
      }

      if (context === 'block') {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          data: {
            ...state.data,
            experimentBlock: {
              //...state.data.search,
              [context]: {
                //...(state.data.search[context] || {}),
                ...action.data
              }
            }
          }
        };
      } else {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          data: {
            ...state.data,
            experiment: {
              //...state.data.search,
              [context]: {
                //...(state.data.search[context] || {}),
                ...action.data
              }
            }
          }
        };
      }
    case Constants.REQUEST_EXPERIMENTS:
      if (context) {
        loadContext[context] = true;
      }
      return {
        ...state,
        isLoading: true,
        hasLoadedOnce: true,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.REQUEST_ARCHIVED_EXPERIMENTS:
      if (context) {
        loadContext[context] = true;
      }
      return {
        ...state,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.RECEIVE_EXPERIMENTS:
      if (context) {
        loadContext[context] = false;
      }
      if (action.error) {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          error: action.error
        };
      }

      const previousExperiments = state.data.search[context]?.experiments || [];
      const previousExperimentPaging = state.data.search[context]?.paging;

      let updatedExperiments = previousExperiments;
      let updatedExperimentPaging = previousExperimentPaging;

      if (action.page?.page) {
        if (action.page.page > (previousExperimentPaging?.page || 0)) {
          updatedExperiments = mergeExperimentLists(
            previousExperiments,
            action.data.experiments
          );
          updatedExperimentPaging = action.data.paging;
        }
        if (action.load?.mode === 'reset') {
          updatedExperiments = [...action.data.experiments];
          updatedExperimentPaging = action.data.paging;
        }
      } else {
        updatedExperiments = [...action.data.experiments];
      }

      return {
        ...state,
        isLoading: false,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        },
        data: {
          ...state.data,
          search: {
            ...state.data.search,
            [context]: {
              paging: updatedExperimentPaging,
              experiments: updatedExperiments
            }
          }
        }
      };
    case Constants.RECEIVE_ARCHIVED_EXPERIMENTS:
      if (context) {
        loadContext[context] = false;
      }
      if (action.error) {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          error: action.error
        };
      }

      return {
        ...state,
        isLoading: false,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        },
        data: {
          ...state.data,
          archived: action.data
        }
      };
    case Constants.REQUEST_SAVE_EXPERIMENT:
      return { ...state, isSaving: true };
    case Constants.RECEIVE_SAVE_EXPERIMENT:
      if (action.error) {
        return {
          ...state,
          isSaving: false,
          data: {
            ...state.data,
            lastSave: {
              error: action.error
            }
          }
        };
      }

      if (action.context === 'overview') {
        let updatedSearch = { ...state.data.search };
        if (action.context) {
          const updatedPaging = state.data.search[action.context]?.paging;
          if (updatedPaging) {
            switch (action.mode) {
              case 'create':
                updatedPaging.total += 1;
                break;
              case 'publish':
              case 'end':
              case 'delete':
              case 'archive':
                updatedPaging.total -= 1;
                if (updatedPaging.total < 0) updatedPaging.total = 0;
                break;
              default:
                break;
            }
          }
          updatedSearch = {
            ...state.data.search,
            [action.context]: {
              paging: updatedPaging,
              experiments: updateExperimentInList(
                state.data.search[action.context].experiments || [],
                action
              )
            }
          };
        }
        let lastSave = { ...state.data.lastSave };
        if (action.mode !== 'refresh') {
          lastSave = {
            mode: action.mode,
            experiments: { ...action.data }
          };
        }
        if (action.mode === 'create' || action.mode === 'delete') {
          lastSave = {
            mode: action.mode,
            experiments: { ...action.experiment }
          };
        }

        return {
          ...state,
          isSaving: false,
          data: {
            ...state.data,
            search: updatedSearch,
            lastSave
          }
        };
      } else if (action.context === 'analytics') {
        let lastSave = { ...state.data.lastSave };
        lastSave = {
          mode: action.mode,
          experiments: { ...action.data }
        };
        if (action.mode === 'delete') {
          lastSave = {
            mode: action.mode,
            experiments: { ...action.experiment }
          };
        }

        return {
          ...state,
          isSaving: false,
          data: {
            ...state.data,
            experiment: {
              analytics: { ...action.data }
            },
            lastSave
          }
        };
      }
      break;
    case Constants.REQUEST_SAVE_SELECTABLE:
      if (context) {
        saveContext[context] = { saveInitiated: true };
      }
      return {
        ...state,
        isSaving: true,
        saveContext: {
          ...state.saveContext,
          ...saveContext
        }
      };
    case Constants.CLEAR_SELECTABLE_SAVE_CONTEXT:
      if (!context) {
        return { ...state, saveContext: {} };
      }
      const clearContext = Array.isArray(action.context)
        ? action.context
        : [action.context];
      const updatedContext = {};
      Object.keys(state.saveContext).forEach(key => {
        if (!clearContext.includes(key)) {
          updatedContext[key] = state.saveContext[key];
        }
      });
      return { ...state, saveContext: updatedContext };
    case Constants.RECEIVE_SAVE_SELECTABLE:
      if (context) {
        saveContext[context] = { saveCompleted: true };
      }
      if (action.error) {
        if (context) {
          saveContext[context] = { error: action.error };
        }
        return {
          ...state,
          isSaving: false,
          saveContext: {
            ...state.saveContext,
            ...saveContext
          },
          data: {
            ...state.data,
            lastSave: {
              error: action.error
            }
          }
        };
      }
      if (action.context === 'analytics') {
        let lastSave = { ...state.data.lastSave };
        lastSave = {
          mode: action.mode,
          selectable: { ...action.data }
        };
        return {
          ...state,
          isSaving: false,
          data: {
            ...state.data,
            lastSave
          }
        };
      }
      return {
        ...state,
        isSaving: false,
        saveContext: {
          ...state.saveContext,
          ...saveContext
        },
        data: {
          ...state.data
        }
      };
    case Constants.UPDATE_EXPERIMENT_FILTER:
      if (action.error) {
        return { ...state, isLoading: false, error: action.error };
      }
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          browsedFilter: {
            ...state.data.browsedFilter,
            ...action.filter
          }
        }
      };
    case Constants.MODIFY_EXPERIMENT_ANALYTICS_FILTER:
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          analytics: {
            ...state.data.analytics,
            filter: {
              ...state.data.analytics.filter,
              ...action.filter
            }
          }
        }
      };
    case Constants.RECEIVE_EXPERIMENT_PERFORMANCE:
      if (action.error) {
        return { ...state, isLoading: false, error: action.error };
      }
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          analytics: {
            ...state.data.analytics,
            performance: {
              details: action.data?.details || [],
              performers: action.data?.performers || [],
              summary: action.data?.summary || {}
            }
          }
        }
      };
    case Constants.REQUEST_EXPERIMENT_JOBS:
      return { ...state, jobsLoading: true };
    case Constants.RECEIVE_EXPERIMENT_JOBS:
      if (action.error) {
        return { ...state, jobsLoading: false, error: action.error };
      }
      return {
        ...state,
        jobsLoading: false,
        data: {
          ...state.data,
          analytics: {
            ...state.data.analytics,
            jobs: [...action.data]
          }
        }
      };
    default:
      return state;
  }
}
